// Wishlist Manager - Integration with Wishlist Power API
// API: window.Maestrooo.wishlist

class WishlistManager {
  constructor() {
    this.isReady = false;
    this.init();
  }

  async init() {
    try {
      // Wait for Wishlist Power API to be ready
      await this.waitForAPI();
      this.isReady = true;
      this.setupEventListeners();
      console.log('WishlistManager: Successfully initialized');
    } catch (error) {
      console.error('WishlistManager: Failed to initialize:', error);
      this.isReady = false;
    }
  }

  async waitForAPI() {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds

      const checkAPI = () => {
        attempts++;

        // Check if API exists AND methods are actually functions
        if (
          window.Maestrooo &&
          window.Maestrooo.wishlist &&
          typeof window.Maestrooo.wishlist.getContent === 'function'
        ) {
          resolve();
        } else {
          if (attempts >= maxAttempts) {
            console.error('Wishlist Power API not loaded. Make sure App Embed is enabled in Theme Customizer.');
            reject(new Error('Wishlist Power API not available'));
          } else {
            setTimeout(checkAPI, 100);
          }
        }
      };
      checkAPI();
    });
  }

  setupEventListeners() {
    // Listen for product added to wishlist
    document.addEventListener('ooo-wishlist:product-added', (event) => {
      this.onWishlistChanged(event.detail);
    });

    // Listen for product removed from wishlist
    document.addEventListener('ooo-wishlist:product-removed', (event) => {
      this.onWishlistChanged(event.detail);
    });

    // Event delegation for wishlist toggle buttons
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-wishlist-toggle]');
      if (button) {
        event.preventDefault();
        const handle = button.dataset.productHandle;
        const variantId = button.dataset.variantId || null;
        if (handle) {
          this.toggleProduct(handle, variantId);
        }
      }
    });
  }

  onWishlistChanged(detail) {
    // Update counter in header
    this.updateCounter(detail.wishlist_products_count);

    // Update all product buttons
    this.updateAllProductButtons();
  }

  updateCounter(count) {
    const counter = document.querySelector('#wishlist-counter');
    if (counter) {
      counter.textContent = `(${count})`;
    }
  }

  async updateAllProductButtons() {
    const buttons = document.querySelectorAll('[data-wishlist-toggle]');
    for (const button of buttons) {
      const handle = button.dataset.productHandle;
      if (handle) {
        await this.updateButtonState(button, handle);
      }
    }
  }

  async updateButtonState(button, handle) {
    try {
      const isInWishlist = await window.Maestrooo.wishlist.containsProduct(handle);
      button.textContent = isInWishlist ? window.themeStrings.wishlist.remove : window.themeStrings.wishlist.add;
      button.classList.toggle('in-wishlist', isInWishlist);
    } catch (error) {
      console.error('Error checking wishlist status:', error);
    }
  }

  /**
   * PN-057: zwraca STAN PO operacji, żeby UI nie kłamał:
   *   true  — produkt jest teraz w ulubionych
   *   false — produktu już nie ma w ulubionych
   *   null  — operacja się NIE udała (brak API albo błąd); wołający nie ma prawa
   *           ruszyć swojego stanu.
   * Wcześniej metoda nic nie zwracała i — w odróżnieniu od bliźniaczych
   * `getWishlistCount()` / `getWishlistProducts()` — nie sprawdzała, czy API w ogóle
   * istnieje. Leciał wtedy goły TypeError, a serduszko i tak zapalało się optymistycznie.
   * To realny scenariusz produkcyjny, nie tylko dev: Cookiebot ma `blockingmode="auto"`,
   * więc do momentu zgody na ciasteczka aplikacja wishlisty jest zablokowana.
   */
  async toggleProduct(handle, variantId = null) {
    try {
      if (!window.Maestrooo?.wishlist?.containsProduct) {
        console.error('Wishlist API not available — pomijam toggleProduct dla', handle);
        return null;
      }

      const isInWishlist = await window.Maestrooo.wishlist.containsProduct(handle);

      if (isInWishlist) {
        await window.Maestrooo.wishlist.removeProduct(handle, variantId);

        // Manually emit event for our components
        const event = new CustomEvent('ooo-wishlist:product-removed', {
          detail: {
            product: { handle },
            wishlist_products_count: await this.getWishlistCount()
          }
        });
        window.dispatchEvent(event);
        return false;
      } else {
        await window.Maestrooo.wishlist.addProduct(handle, variantId);

        // Manually emit event for our components
        const event = new CustomEvent('ooo-wishlist:product-added', {
          detail: {
            product: { handle },
            wishlist_products_count: await this.getWishlistCount()
          }
        });
        window.dispatchEvent(event);
        return true;
      }
    } catch (error) {
      console.error('Error toggling wishlist product:', error);
      return null;
    }
  }

  async getWishlistCount() {
    try {
      if (!window.Maestrooo?.wishlist?.getContent) {
        console.error('Wishlist API not available');
        return 0;
      }
      const { products } = await window.Maestrooo.wishlist.getContent();
      return products.length;
    } catch (error) {
      console.error('Error getting wishlist count:', error);
      return 0;
    }
  }

  async getWishlistProducts() {
    try {
      if (!window.Maestrooo?.wishlist?.getContent) {
        throw new Error('Wishlist Power API not available. Check if the app is properly installed.');
      }
      const result = await window.Maestrooo.wishlist.getContent();
      return result.products || [];
    } catch (error) {
      console.error('Error getting wishlist products:', error);
      throw error; // Re-throw so the caller can handle it
    }
  }

  async containsProduct(handle) {
    try {
      if (!window.Maestrooo?.wishlist?.containsProduct) {
        console.error('Wishlist API not available');
        return false;
      }
      return await window.Maestrooo.wishlist.containsProduct(handle);
    } catch (error) {
      console.error('Error checking if product in wishlist:', error);
      return false;
    }
  }

  async initCounter() {
    const count = await this.getWishlistCount();
    this.updateCounter(count);
  }
}

// Initialize wishlist manager
document.addEventListener('DOMContentLoaded', () => {
  window.wishlistManager = new WishlistManager();

  // Initialize counter after manager is ready
  const checkReady = setInterval(() => {
    if (window.wishlistManager.isReady) {
      window.wishlistManager.initCounter();
      window.wishlistManager.updateAllProductButtons();
      clearInterval(checkReady);
    }
  }, 100);
});

// Global function for easy access
window.toggleWishlistProduct = function(handle, variantId = null) {
  if (window.wishlistManager && window.wishlistManager.isReady) {
    window.wishlistManager.toggleProduct(handle, variantId);
  } else {
    // Wait for manager to be ready and try again
    const waitAndRetry = setInterval(() => {
      if (window.wishlistManager && window.wishlistManager.isReady) {
        window.wishlistManager.toggleProduct(handle, variantId);
        clearInterval(waitAndRetry);
      }
    }, 100);

    // Timeout after 5 seconds
    setTimeout(() => clearInterval(waitAndRetry), 5000);
  }
};
